import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X, Users, Check, Calendar, Trash2, MessageSquare, Upload, AtSign, ArrowLeft, Paperclip, Send, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Pulse List ──────────────────────────────────────────────────────────────
function PulseList({ pulses, onSelect, onCreate }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div>
          <h1 style={{ fontSize: "22px", fontWeight: "800", color: "#1B2B22", margin: 0 }}>Pulse</h1>
          <p style={{ fontSize: "12px", color: "#64748B", marginTop: "2px" }}>Private collaborative task groups</p>
        </div>
        <button onClick={onCreate} data-testid="create-pulse-btn"
          style={{ padding: "8px 16px", borderRadius: "8px", border: "none", background: "#2F7D52", color: "#fff", fontSize: "12px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "5px" }}>
          <Plus size={14} />New Pulse
        </button>
      </div>
      {pulses.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "48px", textAlign: "center" }}>
          <Users size={32} style={{ color: "#94A3B8", margin: "0 auto 12px" }} />
          <p style={{ fontSize: "14px", color: "#64748B" }}>No Pulses yet</p>
          <p style={{ fontSize: "12px", color: "#94A3B8" }}>Create a Pulse to start collaborating on private tasks</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "12px" }}>
          {pulses.map(p => (
            <div key={p.id} onClick={() => onSelect(p)} data-testid={`pulse-card-${p.id}`}
              style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "16px", cursor: "pointer", transition: "box-shadow 0.15s, transform 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
              <div style={{ fontSize: "15px", fontWeight: "700", color: "#1B2B22", marginBottom: "6px" }}>{p.name}</div>
              {p.description && <div style={{ fontSize: "11px", color: "#64748B", marginBottom: "8px" }}>{p.description}</div>}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#64748B" }}>
                  <Users size={11} />{p.member_details?.length || 0} members
                </div>
                <div style={{ fontSize: "11px", color: "#64748B" }}>
                  {p.done_count || 0}/{p.task_count || 0} done
                </div>
              </div>
              {(p.task_count || 0) > 0 && (
                <div style={{ marginTop: "8px", height: "3px", borderRadius: "2px", background: "#E2E8F0" }}>
                  <div style={{ height: "100%", borderRadius: "2px", background: "#2F7D52", width: `${Math.round((p.done_count / p.task_count) * 100)}%`, transition: "width 0.3s" }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Create Pulse Modal ──────────────────────────────────────────────────────
function CreatePulseModal({ onClose, onCreated, teamMembers, currentUserId }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (selected.length < 1) { toast.error("Invite at least 1 member"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/pulse`, { name, description: desc, member_ids: selected });
      toast.success("Pulse created");
      onCreated(data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: "14px", padding: "24px", width: "440px", maxHeight: "80vh", overflowY: "auto" }} data-testid="create-pulse-modal">
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "800", color: "#1B2B22" }}>New Pulse</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <div style={{ marginBottom: "12px" }}>
          <label style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", display: "block", marginBottom: "4px" }}>Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Daily Standup, Site Coordination..."
            style={{ width: "100%", padding: "8px 12px", borderRadius: "7px", border: "1px solid #D1D5DB", fontSize: "13px" }} />
        </div>
        <div style={{ marginBottom: "12px" }}>
          <label style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", display: "block", marginBottom: "4px" }}>Description (optional)</label>
          <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="What's this Pulse about?"
            style={{ width: "100%", padding: "8px 12px", borderRadius: "7px", border: "1px solid #D1D5DB", fontSize: "13px" }} />
        </div>
        <div style={{ marginBottom: "16px" }}>
          <label style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", display: "block", marginBottom: "6px" }}>Invite Members</label>
          <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: "7px" }}>
            {teamMembers.filter(m => m.id !== currentUserId).map(m => (
              <div key={m.id} onClick={() => toggle(m.id)}
                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", cursor: "pointer", background: selected.includes(m.id) ? "#ECFDF5" : "#fff", borderBottom: "1px solid #F1F5F9" }}>
                <div style={{ width: "18px", height: "18px", borderRadius: "4px", border: selected.includes(m.id) ? "none" : "1.5px solid #D1D5DB", background: selected.includes(m.id) ? "#2F7D52" : "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {selected.includes(m.id) && <Check size={12} color="#fff" />}
                </div>
                <div>
                  <div style={{ fontSize: "12px", fontWeight: "600", color: "#1B2B22" }}>{m.full_name || m.name}</div>
                  <div style={{ fontSize: "10px", color: "#64748B" }}>{m.email}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: "10px", color: "#94A3B8", marginTop: "4px" }}>You + {selected.length} selected = {selected.length + 1} members</div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: "8px", border: "1px solid #D1D5DB", background: "#fff", fontSize: "12px", cursor: "pointer" }}>Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="submit-pulse-btn"
            style={{ padding: "8px 20px", borderRadius: "8px", border: "none", background: "#2F7D52", color: "#fff", fontSize: "12px", cursor: "pointer", fontWeight: "700" }}>
            {saving ? "Creating..." : "Create Pulse"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Task Comment Thread ─────────────────────────────────────────────────────
function CommentThread({ pulseId, taskId, members }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQ, setMentionQ] = useState("");
  const inputRef = useRef(null);

  const fetchComments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/pulse/${pulseId}/tasks/${taskId}/comments`);
      setComments(data);
    } catch (err) { console.error(err); }
  }, [pulseId, taskId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const lastAt = val.lastIndexOf("@");
    if (lastAt >= 0 && (lastAt === val.length - 1 || val.slice(lastAt).match(/^@\w*$/))) {
      setMentionQ(val.slice(lastAt + 1).toLowerCase());
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const insertMention = (m) => {
    const lastAt = text.lastIndexOf("@");
    setText(text.slice(0, lastAt) + `@${m.name || m.full_name} `);
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  const sendComment = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const mentions = (text.match(/@[\w\s]+/g) || []).map(m => m.slice(1).trim());
      await axios.post(`${API}/api/pulse/${pulseId}/tasks/${taskId}/comments`, { text, mentions });
      setText("");
      fetchComments();
    } catch (err) { console.error(err); toast.error("Failed to post comment"); }
    setSending(false);
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data: uploaded } = await axios.post(`${API}/api/pulse/${pulseId}/upload`, formData);
      await axios.post(`${API}/api/pulse/${pulseId}/tasks/${taskId}/comments`, {
        text: `Shared file: ${uploaded.file_name}`,
        file_url: uploaded.file_url,
        file_name: uploaded.file_name,
      });
      fetchComments();
      toast.success("File shared");
    } catch (err) { console.error(err); toast.error("Upload failed"); }
    e.target.value = "";
  };

  const filteredMembers = members.filter(m => (m.name || m.full_name || "").toLowerCase().includes(mentionQ));

  return (
    <div style={{ marginTop: "8px", borderTop: "1px solid #F1F5F9", paddingTop: "8px" }}>
      {comments.map(c => (
        <div key={c.id} style={{ marginBottom: "6px", fontSize: "11px" }}>
          <span style={{ fontWeight: "700", color: "#1B2B22" }}>{c.author_name}</span>
          <span style={{ color: "#94A3B8", marginLeft: "6px" }}>{new Date(c.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
          <div style={{ color: "#475569", marginTop: "2px", whiteSpace: "pre-wrap" }}>{c.text}</div>
          {c.file_url && (
            <a href={`${API}${c.file_url}`} target="_blank" rel="noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "10px", color: "#2F7D52", marginTop: "2px", textDecoration: "none" }}>
              <Paperclip size={9} />{c.file_name}
            </a>
          )}
        </div>
      ))}
      <div style={{ display: "flex", gap: "4px", alignItems: "center", position: "relative" }}>
        <div style={{ flex: 1, position: "relative" }}>
          <input ref={inputRef} value={text} onChange={handleTextChange} placeholder="Comment... use @ to mention"
            onKeyDown={e => e.key === "Enter" && !mentionOpen && sendComment()}
            style={{ width: "100%", padding: "6px 8px", borderRadius: "6px", border: "1px solid #E2E8F0", fontSize: "11px" }} />
          {mentionOpen && filteredMembers.length > 0 && (
            <div style={{ position: "absolute", bottom: "100%", left: 0, background: "#fff", border: "1px solid #E2E8F0", borderRadius: "6px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", maxHeight: "120px", overflowY: "auto", width: "200px", zIndex: 10 }}>
              {filteredMembers.map(m => (
                <div key={m.id} onClick={() => insertMention(m)}
                  style={{ padding: "6px 10px", cursor: "pointer", fontSize: "11px", color: "#1B2B22" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#F0F7F2"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}>
                  {m.name || m.full_name}
                </div>
              ))}
            </div>
          )}
        </div>
        <label style={{ cursor: "pointer", color: "#94A3B8", padding: "4px" }}>
          <Paperclip size={14} />
          <input type="file" style={{ display: "none" }} onChange={uploadFile} />
        </label>
        <button onClick={sendComment} disabled={sending} style={{ background: "#2F7D52", color: "#fff", border: "none", borderRadius: "6px", padding: "5px 8px", cursor: "pointer" }}>
          <Send size={12} />
        </button>
      </div>
    </div>
  );
}

// ── Pulse Detail ────────────────────────────────────────────────────────────
function PulseDetail({ pulse: initialPulse, onBack, teamMembers, currentUserId }) {
  const [pulse, setPulse] = useState(initialPulse);
  const [newTask, setNewTask] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [newDue, setNewDue] = useState("");
  const [expandedTask, setExpandedTask] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/pulse/${pulse.id}`);
      setPulse(data);
    } catch (err) { console.error(err); }
  }, [pulse.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const addTask = async () => {
    if (!newTask.trim()) return;
    const assigneeMember = pulse.member_details?.find(m => m.id === newAssignee);
    try {
      await axios.post(`${API}/api/pulse/${pulse.id}/tasks`, {
        title: newTask, assigned_to: newAssignee || null, assigned_to_name: assigneeMember?.name || "", due_date: newDue || null,
      });
      setNewTask(""); setNewAssignee(""); setNewDue("");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggleDone = async (task) => {
    try {
      await axios.put(`${API}/api/pulse/${pulse.id}/tasks/${task.id}`, { is_done: !task.is_done });
      refresh();
    } catch (err) { console.error(err); }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await axios.delete(`${API}/api/pulse/${pulse.id}/tasks/${taskId}`);
      refresh();
    } catch (err) { console.error(err); }
  };

  const tasks = pulse.tasks || [];
  const pending = tasks.filter(t => !t.is_done);
  const done = tasks.filter(t => t.is_done);

  return (
    <div>
      <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: "4px", background: "none", border: "none", cursor: "pointer", color: "#64748B", fontSize: "12px", marginBottom: "12px", padding: 0 }}>
        <ArrowLeft size={14} />Back to Pulses
      </button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "16px" }}>
        <div>
          <h1 style={{ fontSize: "22px", fontWeight: "800", color: "#1B2B22", margin: 0 }}>{pulse.name}</h1>
          {pulse.description && <p style={{ fontSize: "12px", color: "#64748B", marginTop: "2px" }}>{pulse.description}</p>}
          <div style={{ display: "flex", gap: "8px", marginTop: "6px", flexWrap: "wrap" }}>
            {pulse.member_details?.map(m => (
              <span key={m.id} style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "10px", background: "#ECFDF5", color: "#065F46", fontWeight: "600" }}>{m.name}</span>
            ))}
          </div>
        </div>
        <div style={{ fontSize: "12px", color: "#64748B" }}>{done.length}/{tasks.length} done</div>
      </div>

      {/* Add task row */}
      <div data-testid="pulse-add-task-row" style={{ display: "flex", gap: "6px", marginBottom: "16px", alignItems: "center", background: "#F5F8F5", padding: "10px 12px", borderRadius: "8px", flexWrap: "wrap" }}>
        <input value={newTask} onChange={e => setNewTask(e.target.value)} placeholder="Add a task..."
          onKeyDown={e => e.key === "Enter" && addTask()}
          style={{ flex: "1 1 100%", minWidth: 0, padding: "7px 10px", borderRadius: "6px", border: "1px solid #D1D5DB", fontSize: "12px" }} />
        <select value={newAssignee} onChange={e => setNewAssignee(e.target.value)}
          style={{ flex: "1 1 auto", padding: "7px 8px", borderRadius: "6px", border: "1px solid #D1D5DB", fontSize: "11px", minWidth: 0, maxWidth: "140px" }}>
          <option value="">Unassigned</option>
          {pulse.member_details?.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <input type="date" value={newDue} onChange={e => setNewDue(e.target.value)}
          style={{ flex: "0 0 auto", padding: "6px 8px", borderRadius: "6px", border: "1px solid #D1D5DB", fontSize: "11px" }} />
        <button onClick={addTask} data-testid="add-pulse-task-btn"
          style={{ padding: "7px 14px", borderRadius: "6px", background: "#2F7D52", color: "#fff", border: "none", fontSize: "11px", cursor: "pointer", fontWeight: "600" }}>Add</button>
      </div>

      {/* Pending tasks */}
      {pending.map(t => (
        <div key={t.id} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "8px", padding: "10px 14px", marginBottom: "6px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div onClick={() => toggleDone(t)} style={{ width: "18px", height: "18px", borderRadius: "4px", border: "1.5px solid #D1D5DB", cursor: "pointer", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#1B2B22" }}>{t.title}</div>
              <div style={{ display: "flex", gap: "8px", marginTop: "2px", fontSize: "10px", color: "#64748B" }}>
                {t.assigned_to_name && <span>Assigned: {t.assigned_to_name}</span>}
                {t.due_date && <span>Due: {new Date(t.due_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>}
              </div>
            </div>
            <button onClick={() => setExpandedTask(expandedTask === t.id ? null : t.id)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", padding: "2px" }}>
              <MessageSquare size={14} />
            </button>
            <button onClick={() => deleteTask(t.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#D1D5DB", padding: "2px" }}>
              <Trash2 size={13} />
            </button>
          </div>
          {expandedTask === t.id && <CommentThread pulseId={pulse.id} taskId={t.id} members={pulse.member_details || []} />}
        </div>
      ))}

      {/* Completed */}
      {done.length > 0 && (
        <div style={{ marginTop: "16px" }}>
          <div style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", textTransform: "uppercase", marginBottom: "6px" }}>Completed ({done.length})</div>
          {done.map(t => (
            <div key={t.id} style={{ background: "#FAFAFA", border: "1px solid #F1F5F9", borderRadius: "8px", padding: "8px 14px", marginBottom: "4px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div onClick={() => toggleDone(t)} style={{ width: "18px", height: "18px", borderRadius: "4px", background: "#2F7D52", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Check size={12} color="#fff" />
                </div>
                <span style={{ fontSize: "12px", color: "#94A3B8", textDecoration: "line-through" }}>{t.title}</span>
                <button onClick={() => setExpandedTask(expandedTask === t.id ? null : t.id)}
                  style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#D1D5DB", padding: "2px" }}>
                  <MessageSquare size={13} />
                </button>
              </div>
              {expandedTask === t.id && <CommentThread pulseId={pulse.id} taskId={t.id} members={pulse.member_details || []} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────
export default function PulsePage() {
  const { user } = useAuth();
  const [pulses, setPulses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPulse, setSelectedPulse] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);

  const fetchPulses = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/pulse`);
      setPulses(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  const fetchTeam = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/users`);
      setTeamMembers(data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchPulses(); fetchTeam(); }, [fetchPulses, fetchTeam]);

  if (loading) return <div style={{ padding: "24px", color: "#94A3B8" }}>Loading...</div>;

  return (
    <div style={{ padding: "20px 24px", fontFamily: "Manrope, sans-serif", maxWidth: "900px" }} data-testid="pulse-page">
      {selectedPulse ? (
        <PulseDetail pulse={selectedPulse} onBack={() => { setSelectedPulse(null); fetchPulses(); }} teamMembers={teamMembers} currentUserId={user?.id} />
      ) : (
        <PulseList pulses={pulses} onSelect={setSelectedPulse} onCreate={() => setShowCreate(true)} />
      )}
      {showCreate && <CreatePulseModal onClose={() => setShowCreate(false)} onCreated={(p) => { fetchPulses(); setSelectedPulse(p); }} teamMembers={teamMembers} currentUserId={user?.id} />}
    </div>
  );
}
